import {
    ArrowDownwardRounded,
    ArrowUpwardRounded,
    CheckRounded,
    CloseRounded,
    CodeRounded,
    ContentCopyRounded,
    DeleteOutlineRounded,
    FormatAlignCenterRounded,
    FormatAlignLeftRounded,
    FormatAlignRightRounded,
    FormatBoldRounded,
    FormatClearRounded,
    FormatColorFillRounded,
    FormatColorTextRounded,
    FormatItalicRounded,
    FormatSizeRounded,
    FormatUnderlinedRounded,
    ImageRounded,
    LinkRounded,
    StrikethroughSRounded,
} from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, TextField, Tooltip, alpha, useTheme } from "@mui/material";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ImageGallery from "../../themebuilder/ImageGallery";

/**
 * Element-level editing for imported HTML (raw-html blocks) in the site builder.
 * A single click selects an element and shows one toolbar: text formatting
 * (applied to the selected text if there is a selection, otherwise to the whole
 * element) plus element actions. Edits go to the live DOM and are reported as
 * the block's new innerHTML through onChange.
 */

const TEXT_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a', 'button', 'li', 'label', 'strong', 'em', 'b', 'i', 'small', 'blockquote', 'td', 'th'];
const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64];

// Index path from root -> element, so the selection survives React re-setting innerHTML
const getPath = (root, el) => {
    const path = [];
    let node = el;
    while (node && node !== root) {
        const parent = node.parentElement;
        if (!parent) return null;
        path.unshift(Array.prototype.indexOf.call(parent.children, node));
        node = parent;
    }
    return node === root ? path : null;
};

const resolvePath = (root, path) => {
    let node = root;
    for (const idx of path || []) {
        node = node?.children?.[idx];
        if (!node) return null;
    }
    return node === root ? null : node;
};

const toHex = (color) => {
    if (!color) return '#000000';
    if (color.startsWith('#')) return color.length === 4 ? `#${[...color.slice(1)].map((c) => c + c).join('')}` : color.slice(0, 7);
    const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return '#000000';
    return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
};

const normalizeUrl = (raw = '') => {
    const val = String(raw).trim().replace(/\s/g, '');
    if (!val) return '';
    if (val.startsWith('/') || val.startsWith('#') || /^[a-z][a-z0-9+.-]*:/i.test(val)) return val;
    return `https://${val}`;
};

// Native color picker: preview on every drag (DOM only), save once when the picker closes.
// Saving on each `input` event re-renders the builder, which dismisses the native popup.
const ColorInput = ({ value, onPreview, onCommit }) => {
    const inputRef = useRef(null);
    const commitRef = useRef(onCommit);
    commitRef.current = onCommit;

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        const handleChange = (e) => commitRef.current(e.target.value);
        input.addEventListener('change', handleChange);
        return () => input.removeEventListener('change', handleChange);
    }, []);

    return (
        <Box
            component="input"
            ref={inputRef}
            type="color"
            defaultValue={value}
            onInput={(e) => onPreview(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
        />
    );
};

const Sep = () => <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />;

// Selected element path per block, kept outside React state: BuilderPage recreates its
// SortableItem component on every render, so each save remounts this editor.
const selectionStore = new Map();

export default function HtmlElementEditor({ rootRef, html, onChange, blockId }) {
    const theme = useTheme();
    const wrapperRef = useRef(null);
    const savedRangeRef = useRef(null);
    // Link URL / alt text typed but not saved yet (saved on Enter, check, or leaving the field)
    const linkDirtyRef = useRef(false);
    const storeKey = String(blockId);
    const [path, setPathState] = useState(() => selectionStore.get(storeKey) || null);
    const setPath = useCallback((next) => {
        if (next) selectionStore.set(storeKey, next);
        else selectionStore.delete(storeKey);
        setPathState(next);
    }, [storeKey]);
    const [rect, setRect] = useState(null);
    const [, forceRender] = useState(0);
    const [panel, setPanel] = useState(null); // null | 'fontSize' | 'link'
    const [galleryOpen, setGalleryOpen] = useState(null);
    const [linkValue, setLinkValue] = useState('');
    const [altValue, setAltValue] = useState('');
    const [codeOpen, setCodeOpen] = useState(false);
    const [codeValue, setCodeValue] = useState('');

    const getSelected = useCallback(() => (rootRef.current && path ? resolvePath(rootRef.current, path) : null), [rootRef, path]);

    const measure = useCallback(() => {
        const el = getSelected();
        const wrapper = wrapperRef.current?.parentElement;
        if (!el || !wrapper) { setRect(null); return; }
        const r = el.getBoundingClientRect();
        const w = wrapper.getBoundingClientRect();
        setRect({ top: r.top - w.top, left: r.left - w.left, width: r.width, height: r.height });
    }, [getSelected]);

    // Re-measure after every html change (React replaces the nodes) and on resize
    useLayoutEffect(() => { measure(); }, [html, path, measure]);
    useEffect(() => {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        const onClick = (e) => {
            const target = e.target instanceof Element ? e.target : null;
            if (!target || target === root || !root.contains(target)) return;
            // Links inside the builder should select, not navigate
            if (target.closest('a')) e.preventDefault();
            setPath(getPath(root, target));
            setPanel(null);
        };
        root.addEventListener('click', onClick);
        return () => root.removeEventListener('click', onClick);
    }, [rootRef, setPath]);

    // Remember the last text selection inside the block; clicking the toolbar must not lose it
    useEffect(() => {
        const onSelectionChange = () => {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            if (rootRef.current?.contains(range.commonAncestorContainer)) {
                savedRangeRef.current = range.collapsed ? null : range.cloneRange();
            }
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [rootRef]);

    // Deselect when clicking outside this block (but not inside our dialogs/gallery)
    useEffect(() => {
        if (!path) return;
        const onDocDown = (e) => {
            const t = e.target;
            if (!(t instanceof Element)) return;
            if (rootRef.current?.contains(t) || wrapperRef.current?.contains(t)) return;
            if (t.closest('.MuiDialog-root, .MuiPopover-root, .MuiModal-root')) return;
            setPath(null);
            setPanel(null);
        };
        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [path, rootRef, setPath]);

    const commit = (nextPath = path) => {
        if (!rootRef.current) return;
        savedRangeRef.current = null;
        setPath(nextPath);
        onChange(rootRef.current.innerHTML);
        forceRender((n) => n + 1);
    };

    const el = getSelected();
    if (!el) return <Box ref={wrapperRef} sx={{ display: 'none' }} />;

    const tag = el.tagName.toLowerCase();
    const isImage = tag === 'img';
    const link = el.closest('a');
    const isText = !isImage && (TEXT_TAGS.includes(tag) || (el.children.length === 0 && el.textContent.trim() !== ''));
    const computed = window.getComputedStyle(el);

    // Non-collapsed text selection inside the selected element, if any
    const getTextRange = () => {
        const range = savedRangeRef.current;
        return range && !range.collapsed && el.contains(range.commonAncestorContainer) ? range : null;
    };

    const execOnSelection = (cmd, value = null) => {
        const range = getTextRange();
        if (!range) return false;
        const sel = window.getSelection();
        rootRef.current?.focus();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand(cmd, false, value);
        commit();
        return true;
    };

    const applyStyle = (prop, value) => {
        el.style[prop] = value;
        commit();
    };

    // Formatting: selected text -> execCommand, otherwise the whole element
    const toggleFormat = (cmd, prop, isOn, onValue, offValue) => {
        if (execOnSelection(cmd)) return;
        applyStyle(prop, isOn ? offValue : onValue);
    };

    const decorations = computed.textDecorationLine || computed.textDecoration || '';
    const active = {
        bold: Number(computed.fontWeight) >= 600 || computed.fontWeight === 'bold',
        italic: computed.fontStyle === 'italic',
        underline: decorations.includes('underline'),
        strike: decorations.includes('line-through'),
        align: computed.textAlign,
    };

    const toggleDecoration = (cmd, name, isOn) => {
        if (execOnSelection(cmd)) return;
        const parts = new Set(decorations.split(' ').filter((p) => p && p !== 'none'));
        if (isOn) parts.delete(name); else parts.add(name);
        applyStyle('textDecorationLine', parts.size ? [...parts].join(' ') : 'none');
    };

    const setAlign = (value) => {
        // Alignment always applies to the element (block-level)
        applyStyle('textAlign', value);
    };

    const setFontSize = (px) => {
        const range = getTextRange();
        if (range) {
            const span = document.createElement('span');
            span.style.fontSize = `${px}px`;
            span.appendChild(range.extractContents());
            range.insertNode(span);
            commit();
        } else {
            applyStyle('fontSize', `${px}px`);
        }
        setPanel(null);
    };

    const clearFormat = () => {
        if (execOnSelection('removeFormat')) return;
        ['fontWeight', 'fontStyle', 'textDecorationLine', 'textDecoration', 'color', 'fontSize', 'textAlign', 'backgroundColor'].forEach((p) => {
            el.style[p] = '';
        });
        if (!el.getAttribute('style')) el.removeAttribute('style');
        commit();
    };

    const openLinkPanel = () => {
        linkDirtyRef.current = false;
        setLinkValue(link?.getAttribute('href') || '');
        setAltValue(isImage ? el.getAttribute('alt') || '' : '');
        setPanel((p) => (p === 'link' ? null : 'link'));
    };

    const applyLink = () => {
        linkDirtyRef.current = false;
        const url = normalizeUrl(linkValue);
        if (isImage) el.setAttribute('alt', altValue);
        if (link) {
            // Keep the <a> itself (imported buttons carry their classes on it); empty = no href
            if (url) link.setAttribute('href', url);
            else link.removeAttribute('href');
            setPanel(null);
            commit();
            return;
        }
        if (url) {
            setPanel(null);
            if (execOnSelection('createLink', url)) return;
            // No selection: link the element's whole content
            const a = document.createElement('a');
            a.setAttribute('href', url);
            while (el.firstChild) a.appendChild(el.firstChild);
            el.appendChild(a);
        }
        setPanel(null);
        commit();
    };

    const move = (dir) => {
        const sibling = dir < 0 ? el.previousElementSibling : el.nextElementSibling;
        if (!sibling) return;
        if (dir < 0) el.parentElement.insertBefore(el, sibling);
        else el.parentElement.insertBefore(sibling, el);
        commit(getPath(rootRef.current, el));
    };

    const duplicate = () => {
        el.after(el.cloneNode(true));
        commit();
    };

    const remove = () => {
        el.remove();
        commit(null);
    };

    const openCode = () => {
        setCodeValue(el.outerHTML);
        setCodeOpen(true);
    };

    const saveCode = () => {
        const tpl = document.createElement('template');
        tpl.innerHTML = codeValue.trim();
        if (tpl.content.firstElementChild) {
            const p = getPath(rootRef.current, el);
            el.replaceWith(...tpl.content.childNodes);
            commit(p);
        }
        setCodeOpen(false);
    };

    // Same look as FloatingTextToolbar buttons
    const ib = (title, Icon, onClick, { on = false, disabled = false, color } = {}) => (
        <Tooltip title={title} key={title} placement="top">
            <span>
                <IconButton
                    size="small"
                    disabled={disabled}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => { e.stopPropagation(); onClick(); }}
                    sx={{
                        color: color || (on ? 'primary.main' : 'text.primary'),
                        bgcolor: on ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        borderRadius: '50%',
                        p: 0.5,
                        '&:hover': { bgcolor: on ? alpha(theme.palette.primary.main, 0.16) : 'action.hover' },
                    }}
                >
                    <Icon sx={{ fontSize: 18 }} />
                </IconButton>
            </span>
        </Tooltip>
    );

    const colorButton = (title, Icon, prop, cmd, current) => (
        <Tooltip title={title} placement="top">
            <IconButton size="small" component="label" sx={{ position: 'relative', borderRadius: '50%', p: 0.5, color: 'text.primary' }}>
                <Icon sx={{ fontSize: 18 }} />
                <ColorInput
                    value={toHex(current)}
                    onPreview={(color) => {
                        if (!getTextRange()) el.style[prop] = color;
                    }}
                    onCommit={(color) => {
                        if (cmd && execOnSelection(cmd, color)) return;
                        const target = getSelected();
                        if (!target) return;
                        target.style[prop] = color;
                        commit();
                    }}
                />
            </IconButton>
        </Tooltip>
    );

    const toolbarAbove = rect && rect.top >= 56;
    const toolbarTop = rect ? (toolbarAbove ? rect.top - 52 : rect.top + rect.height + 8) : 0;

    return (
        <Box ref={wrapperRef} contentEditable={false} suppressContentEditableWarning>
            {rect && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 1,
                        pointerEvents: 'none',
                        zIndex: 999,
                    }}
                />
            )}

            {rect && (
                <Paper
                    elevation={8}
                    onMouseDown={(e) => { if (!(e.target instanceof HTMLInputElement)) e.preventDefault(); e.stopPropagation(); }}
                    onMouseUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        top: toolbarTop,
                        left: rect.left + rect.width / 2,
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        p: 0.5,
                        borderRadius: panel === 'link' ? 3 : '50px',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        boxShadow: '0px 10px 40px rgba(0,0,0,0.12)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {panel === 'fontSize' ? (
                        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ px: 0.5 }}>
                            {FONT_SIZES.map((size) => (
                                <IconButton
                                    key={size}
                                    size="small"
                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    onClick={() => setFontSize(size)}
                                    sx={{ fontSize: '0.75rem', p: 0.5, minWidth: 28, borderRadius: 1, fontWeight: computed.fontSize === `${size}px` ? 700 : 400 }}
                                >
                                    {size}
                                </IconButton>
                            ))}
                            <Sep />
                            {ib('Close', CloseRounded, () => setPanel(null))}
                        </Stack>
                    ) : (
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                            {isImage && ib('Replace image', ImageRounded, () => setGalleryOpen('image'))}
                            {isImage && ib('Alt text', LinkRounded, openLinkPanel, { on: panel === 'link' })}

                            {isText && colorButton('Text color', FormatColorTextRounded, 'color', 'foreColor', computed.color)}
                            {!isImage && colorButton('Background color', FormatColorFillRounded, 'backgroundColor', 'hiliteColor', computed.backgroundColor)}
                            {!isImage && !isText && ib('Background image', ImageRounded, () => setGalleryOpen('background'))}

                            {isText && (
                                <>
                                    <Sep />
                                    {ib('Font size', FormatSizeRounded, () => setPanel('fontSize'))}
                                    <Sep />
                                    {ib('Bold', FormatBoldRounded, () => toggleFormat('bold', 'fontWeight', active.bold, '700', '400'), { on: active.bold })}
                                    {ib('Italic', FormatItalicRounded, () => toggleFormat('italic', 'fontStyle', active.italic, 'italic', 'normal'), { on: active.italic })}
                                    {ib('Underline', FormatUnderlinedRounded, () => toggleDecoration('underline', 'underline', active.underline), { on: active.underline })}
                                    {ib('Strikethrough', StrikethroughSRounded, () => toggleDecoration('strikeThrough', 'line-through', active.strike), { on: active.strike })}
                                    {ib('Clear style', FormatClearRounded, clearFormat)}
                                    <Sep />
                                    {ib('Align left', FormatAlignLeftRounded, () => setAlign('left'), { on: ['left', 'start'].includes(active.align) })}
                                    {ib('Align center', FormatAlignCenterRounded, () => setAlign('center'), { on: active.align === 'center' })}
                                    {ib('Align right', FormatAlignRightRounded, () => setAlign('right'), { on: ['right', 'end'].includes(active.align) })}
                                    <Sep />
                                    {ib('Link', LinkRounded, openLinkPanel, { on: Boolean(link) || panel === 'link' })}
                                </>
                            )}

                            <Sep />
                            {ib('Move up', ArrowUpwardRounded, () => move(-1), { disabled: !el.previousElementSibling })}
                            {ib('Move down', ArrowDownwardRounded, () => move(1), { disabled: !el.nextElementSibling })}
                            {ib('Duplicate', ContentCopyRounded, duplicate)}
                            {ib('Edit HTML', CodeRounded, openCode)}
                            {ib('Delete', DeleteOutlineRounded, remove, { color: 'error.main' })}
                            {ib('Deselect', CloseRounded, () => setPath(null))}
                        </Stack>
                    )}

                    {panel === 'link' && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1, pt: 1 }}>
                            {isImage ? (
                                <TextField size="small" label="Alt text" value={altValue} autoFocus fullWidth
                                    onChange={(e) => {
                                        linkDirtyRef.current = true;
                                        setAltValue(e.target.value);
                                        el.setAttribute('alt', e.target.value);
                                    }}
                                    onBlur={() => linkDirtyRef.current && applyLink()}
                                    onKeyDown={(e) => e.key === 'Enter' && applyLink()} />
                            ) : (
                                <TextField size="small" label="Link URL" placeholder="https://..." value={linkValue} autoFocus fullWidth
                                    onChange={(e) => {
                                        linkDirtyRef.current = true;
                                        setLinkValue(e.target.value);
                                        // Live on the element; saved to the page on Enter / check / leaving the field
                                        if (link) {
                                            const url = normalizeUrl(e.target.value);
                                            if (url) link.setAttribute('href', url);
                                            else link.removeAttribute('href');
                                        }
                                    }}
                                    // Leaving the field (e.g. clicking Publish) saves what was typed
                                    onBlur={() => linkDirtyRef.current && applyLink()}
                                    onKeyDown={(e) => e.key === 'Enter' && applyLink()} />
                            )}
                            <IconButton size="small" color="primary" onClick={applyLink}><CheckRounded fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => (linkDirtyRef.current ? applyLink() : setPanel(null))}><CloseRounded fontSize="small" /></IconButton>
                        </Stack>
                    )}
                </Paper>
            )}

            <ImageGallery
                showImagePicker={Boolean(galleryOpen)}
                onReset={() => setGalleryOpen(null)}
                onAddImage={(url) => {
                    const target = getSelected();
                    if (target && url) {
                        if (galleryOpen === 'background') {
                            target.style.backgroundImage = `url("${url}")`;
                            target.style.backgroundSize = target.style.backgroundSize || 'cover';
                            target.style.backgroundPosition = target.style.backgroundPosition || 'center';
                        } else if (target.tagName.toLowerCase() === 'img') {
                            target.setAttribute('src', url);
                            target.removeAttribute('srcset');
                        }
                        commit();
                    }
                    setGalleryOpen(null);
                }}
            />

            <Dialog open={codeOpen} onClose={() => setCodeOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Edit HTML</DialogTitle>
                <DialogContent>
                    <TextField
                        value={codeValue}
                        onChange={(e) => setCodeValue(e.target.value)}
                        multiline
                        minRows={10}
                        maxRows={24}
                        fullWidth
                        sx={{ mt: 1, '& textarea': { fontFamily: 'monospace', fontSize: 13 }, bgcolor: alpha(theme.palette.grey[500], 0.04) }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCodeOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveCode}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
